import React, { FC, useCallback, useMemo } from "react";
import { LayoutChangeEvent } from "react-native";
import { FlatList } from "react-native";
import { generatePath, useNavigate } from "react-router";
import { View, useDevice } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { ProductPreview } from "../../../../domain/projections/ProductPreview";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages } from "../../i18n/i18n";
import { Routes } from "../../routing/routes";
import { useTrackSelectAnchorGarment } from "../../tracking/useTrackSelectAnchorGarment";
import { style as lookListStyle } from "./LookList.style";
import { LookCollage, LookForCollage } from "./components/lookCollage/LookCollage";

interface LookListProps {
  readonly looks: LookForCollage[];
  readonly onPressLook: ({ lookId, product }: { lookId: string; product: ProductPreview }) => void;
  readonly onLayoutLooks: (event: LayoutChangeEvent) => void;
}

const LookList: FC<LookListProps> = ({ looks, onPressLook, onLayoutLooks }) => {
  const navigate = useNavigate();
  const {
    session: { user },
    basePath,
    tradename,
  } = useStaticInfo();

  const style = useMemo(() => lookListStyle(), []);

  const cylYourGarmentText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.CYL_YOUR_GARMENT });

  const trackSelectAnchorGarment = useTrackSelectAnchorGarment({
    country: user.country,
    segment: user.segment,
    userId: user.id,
    tradename,
  });

  const { screen } = useDevice();
  const isDesktop = screen.L;

  const handleOnPress = useCallback(
    (lookId: string, variantId: string, purchased?: boolean) => {
      const product = { id: variantId } as ProductPreview;

      if (purchased) {
        trackSelectAnchorGarment({ id: variantId });
      }

      onPressLook({ lookId, product });
      navigate(generatePath(`${basePath}/${Routes.PRODUCT_LOOK_DETAIL}`, { variantId, lookId }), {
        state: { innerNavigation: true },
      });
    },
    [onPressLook, navigate, basePath, trackSelectAnchorGarment],
  );

  return (
    <FlatList
      contentContainerStyle={[style.contentList, isDesktop ? style.contentListDesktop : style.contentListMobile]}
      data={looks}
      keyExtractor={(look) => look.lookId}
      renderItem={({ item: look }) => (
        <View
          style={[style.lookCollageWrapper, !isDesktop ? style.lookCollageWrapperMobile : undefined]}
          testID={`lookId-${look.lookId}`}
        >
          <LookCollage
            badge={cylYourGarmentText}
            look={look}
            onPress={(variantId: string, purchased?: boolean) => {
              handleOnPress(look.lookId, variantId, purchased);
            }}
          />
        </View>
      )}
      onLayout={onLayoutLooks}
    />
  );
};

export { LookList };
